import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  Select,
} from '@/shared/components';
import type { PropertyGroupResponse } from '@/shared/api';
import { mergeTargets } from './objectStructure';

interface Props {
  source: PropertyGroupResponse;
  /** Fehlt, solange die Objektliste lädt. */
  all: readonly PropertyGroupResponse[] | undefined;
  failed?: boolean;
  pending?: boolean;
  onClose: () => void;
  onConfirm: (targetGroupId: string) => void;
}

/**
 * Zwei Objekte, die dasselbe Haus sind, zu einem machen. Die Richtung ist bewusst festgelegt:
 * die Apartments wandern von hier ins Ziel, und dieses Objekt verschwindet — sonst müsste der
 * Nutzer raten, welche Adresse gewinnt.
 */
export function MergeObjectDialog({ source, all, failed, pending, onClose, onConfirm }: Props) {
  const targets = all ? mergeTargets(all, source) : [];
  const [targetId, setTargetId] = useState('');

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>„{source.name}" zusammenführen</DialogTitle>
          <DialogDescription>
            Alle {source.apartments.length}{' '}
            {source.apartments.length === 1 ? 'Apartment' : 'Apartments'} wandern in das gewählte
            Objekt und übernehmen dessen Adresse. „{source.name}" gibt es danach nicht mehr.
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          {!all ? (
            <p className="text-ink-soft text-sm" role={failed ? 'alert' : 'status'}>
              {failed
                ? 'Die Objekte konnten nicht geladen werden. Bitte schließen und erneut versuchen.'
                : 'Mögliche Ziele werden geladen …'}
            </p>
          ) : targets.length === 0 ? (
            <p className="text-ink-soft text-sm">
              Es gibt kein passendes Ziel. Infrage kommt nur ein Objekt derselben Währung und
              Zeitzone, das nicht beim Kanalmanager veröffentlicht ist.
            </p>
          ) : (
            <FormField label="Zielobjekt" required>
              <Select
                value={targetId}
                disabled={pending}
                aria-label="Zielobjekt der Zusammenführung"
                onChange={(event) => setTargetId(event.target.value)}
              >
                <option value="">— bitte wählen —</option>
                {targets.map((target) => (
                  <option key={target.id} value={target.id}>
                    {target.name} · {target.address.street}, {target.address.city}
                  </option>
                ))}
              </Select>
            </FormField>
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={pending}>
            Abbrechen
          </Button>
          <Button
            variant="brand"
            loading={pending}
            disabled={pending || targetId === ''}
            onClick={() => onConfirm(targetId)}
          >
            Zusammenführen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
